import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Form, InputGroup, Spinner, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { listExternalVendors, saveExternalVendor, uploadExternalVendorUpiScanner } from "../../../services/externalVendorApi";

const blankVendor = {
  name: "",
  city: "",
  googleMapLink: "",
  vendorType: "RESTAURANT",
  contactName: "",
  contactNumber: "",
  upiNumber: "",
  upiScannerUrl: "",
  bankAccountHolder: "",
  bankAccountNumber: "",
  bankIfsc: "",
  bankName: "",
  notes: "",
  active: true,
};

// Only http(s) links are opened, so a pasted address or stray text never becomes a link.
const isLink = (value) => /^https?:\/\//i.test(String(value || "").trim());

export default function ExternalVendorsManager() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(blankVendor);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [scannerUploading, setScannerUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setRows(await listExternalVendors());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => [row.name, row.city, row.vendorType, row.contactName, row.contactNumber]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(q)));
  }, [rows, search]);

  const patch = (field, value) => setEditing((prev) => ({ ...prev, [field]: value }));

  const uploadScanner = async (file) => {
    if (!file) return;
    setScannerUploading(true);
    try {
      patch("upiScannerUrl", await uploadExternalVendorUpiScanner(file));
      toast.success("Vendor UPI scanner uploaded");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Could not upload vendor UPI scanner");
    } finally {
      setScannerUploading(false);
    }
  };

  const save = async () => {
    if (!editing.name.trim() || !editing.city.trim()) {
      toast.error("Vendor name and city are required");
      return;
    }
    setSaving(true);
    try {
      await saveExternalVendor(editing);
      setEditing(blankVendor);
      await load();
      toast.success("External vendor saved");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="shadow-sm">
      <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div>
          <div className="fw-semibold">External Vendor</div>
          <div className="small text-muted">Restaurants and third-party facility providers used for included external costs.</div>
        </div>
        <InputGroup size="sm" style={{ maxWidth: 320 }}>
          <InputGroup.Text>Search</InputGroup.Text>
          <Form.Control value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, city, phone" />
        </InputGroup>
      </Card.Header>
      <Card.Body>
        <div className="border rounded p-3 mb-3 bg-light">
          <div className="fw-semibold mb-2">{editing.id ? "Edit Vendor" : "Add Vendor"}</div>
          <div className="row g-2">
            <div className="col-lg-3 col-md-6"><Form.Label>Name *</Form.Label><Form.Control value={editing.name} onChange={(e) => patch("name", e.target.value)} /></div>
            <div className="col-lg-2 col-md-6"><Form.Label>City *</Form.Label><Form.Control value={editing.city} onChange={(e) => patch("city", e.target.value)} /></div>
            <div className="col-lg-2 col-md-6"><Form.Label>Type</Form.Label><Form.Select value={editing.vendorType || "OTHER"} onChange={(e) => patch("vendorType", e.target.value)}><option value="RESTAURANT">Restaurant</option><option value="ACTIVITY">Activity</option><option value="OTHER">Other</option></Form.Select></div>
            <div className="col-lg-2 col-md-6"><Form.Label>Contact Name</Form.Label><Form.Control value={editing.contactName || ""} onChange={(e) => patch("contactName", e.target.value)} /></div>
            <div className="col-lg-3 col-md-6"><Form.Label>Contact Number</Form.Label><Form.Control value={editing.contactNumber || ""} onChange={(e) => patch("contactNumber", e.target.value)} /></div>
            <div className="col-lg-3 col-md-6"><Form.Label>UPI ID</Form.Label><Form.Control value={editing.upiNumber || ""} onChange={(e) => patch("upiNumber", e.target.value)} /></div>
            <div className="col-lg-3 col-md-6">
              <Form.Label>Upload UPI Scanner</Form.Label>
              <Form.Control type="file" accept="image/*" disabled={scannerUploading} onChange={(e) => uploadScanner(e.target.files?.[0])} />
              {editing.upiScannerUrl && <a href={editing.upiScannerUrl} target="_blank" rel="noreferrer" className="small">View uploaded scanner</a>}
            </div>
            <div className="col-lg-3 col-md-6"><Form.Label>UPI Scanner URL</Form.Label><Form.Control value={editing.upiScannerUrl || ""} readOnly placeholder="Uploaded scanner URL" /></div>
            <div className="col-lg-3 col-md-6"><Form.Label>Account Holder</Form.Label><Form.Control value={editing.bankAccountHolder || ""} onChange={(e) => patch("bankAccountHolder", e.target.value)} /></div>
            <div className="col-lg-3 col-md-6"><Form.Label>Account Number</Form.Label><Form.Control value={editing.bankAccountNumber || ""} onChange={(e) => patch("bankAccountNumber", e.target.value)} /></div>
            <div className="col-lg-3 col-md-6"><Form.Label>IFSC</Form.Label><Form.Control value={editing.bankIfsc || ""} onChange={(e) => patch("bankIfsc", e.target.value)} /></div>
            <div className="col-lg-3 col-md-6"><Form.Label>Bank Name</Form.Label><Form.Control value={editing.bankName || ""} onChange={(e) => patch("bankName", e.target.value)} /></div>
            <div className="col-lg-3 col-md-6">
              <Form.Label>Google Maps Location</Form.Label>
              <Form.Control value={editing.googleMapLink || ""} onChange={(e) => patch("googleMapLink", e.target.value)} placeholder="https://maps.app.goo.gl/..." />
              {isLink(editing.googleMapLink) && <a href={editing.googleMapLink.trim()} target="_blank" rel="noreferrer" className="small">Open map</a>}
            </div>
            <div className="col-lg-4 col-md-6"><Form.Label>Notes</Form.Label><Form.Control value={editing.notes || ""} onChange={(e) => patch("notes", e.target.value)} /></div>
            <div className="col-lg-2 col-md-6 d-flex align-items-end"><Form.Check type="switch" label="Active" checked={editing.active !== false} onChange={(e) => patch("active", e.target.checked)} /></div>
            <div className="col-lg-3 col-md-6 d-flex align-items-end gap-2">
              <Button onClick={save} disabled={saving}>{saving ? "Saving..." : "Save Vendor"}</Button>
              {editing.id && <Button variant="outline-secondary" onClick={() => setEditing(blankVendor)}>Cancel</Button>}
            </div>
          </div>
        </div>

        {loading ? <div className="text-muted"><Spinner size="sm" className="me-2" />Loading vendors...</div> : (
          <div className="table-responsive">
            <Table bordered hover size="sm" className="align-middle mb-0">
              <thead><tr><th style={{ width: 44 }}>#</th><th>Name</th><th>City</th><th>Type</th><th>Contact</th><th>Payment</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {filtered.map((row, index) => (
                  <tr key={row.id}><td className="small fw-semibold">{index + 1}</td>
                    <td className="fw-semibold">{row.name}</td>
                    <td>{row.city}{isLink(row.googleMapLink) && <div><a href={row.googleMapLink.trim()} target="_blank" rel="noreferrer" className="small">Open map</a></div>}</td>
                    <td>{String(row.vendorType || "OTHER").replaceAll("_", " ")}</td>
                    <td>{[row.contactName, row.contactNumber].filter(Boolean).join(" - ") || "-"}</td>
                    <td>{row.upiNumber || row.bankAccountNumber || row.upiScannerUrl ? "Available" : "Not added"}{row.upiScannerUrl && <div className="small text-muted">Scanner uploaded</div>}</td>
                    <td><Badge bg={row.active === false ? "secondary" : "success"}>{row.active === false ? "Inactive" : "Active"}</Badge></td>
                    <td><Button size="sm" variant="outline-primary" onClick={() => setEditing({ ...blankVendor, ...row })}>Edit</Button></td>
                  </tr>
                ))}
                {!filtered.length && <tr><td colSpan={8} className="text-center text-muted">No vendors found.</td></tr>}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}